import React, { useRef, useEffect, useState, type ReactNode, memo } from "react";
import { ANIOS_THEME_UNDERLAY_LIGHT, mixHexColors } from "@/config/themeWallpapers";
import { generateColorsFromSeed, hashString } from "../../../utils/colorUtils";

export type GradientBlobSurface = "inline" | "desktop";

interface GradientBlobContainerProps {
  children?: ReactNode;
  seed: string;
  animated?: boolean;
  isPaused?: boolean;
  className?: string;
  position?: "tl" | "t" | "tr" | "l" | "c" | "r" | "bl" | "b" | "br";
  dark?: boolean;
  animationDuration?: number;
  themeColors?: string[]; // Optional theme colors to use instead of seed-based colors
  eager?: boolean; // Skip deferred loading and render immediately (for above-the-fold content)
  /**
   * Rasterize the (static, non-animated) SVG filter to a one-time PNG bitmap and
   * render it as a background image. The expensive `feTurbulence`/`feDisplacementMap`
   * chain then runs once instead of living in the compositor for every repaint —
   * a large win for full-screen backgrounds on low-end GPUs. Ignored when `animated`.
   */
  rasterize?: boolean;
  /** `desktop` = full-screen aniOS wallpaper; softer light-mode washes. */
  surface?: GradientBlobSurface;
}

/** Fixed bitmap resolution for rasterized blobs — soft watercolor, so modest is fine. */
const RASTER_PX = 800;

const GradientBlobContainer = memo(function GradientBlobContainer({
  children,
  seed,
  animated = false,
  isPaused = true,
  className,
  position = "c",
  dark = false,
  animationDuration,
  themeColors,
  eager = false,
  rasterize = false,
  surface = "inline",
}: GradientBlobContainerProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(eager);
  const [rasterUrl, setRasterUrl] = useState<string | null>(null);
  const [rasterFailed, setRasterFailed] = useState(false);
  const useRaster = rasterize && !animated;

  // Defer SVG rendering until visible using IntersectionObserver
  useEffect(() => {
    if (eager || isVisible) return;

    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" } // Start loading slightly before visible
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [eager, isVisible]);

  useEffect(() => {
    if (animated && svgRef.current) {
      if (isPaused) {
        svgRef.current.pauseAnimations();
      } else {
        svgRef.current.unpauseAnimations();
      }
    }
  }, [isPaused, animated]);

  const hash = hashString(seed);

  // Use theme colors if provided, otherwise use seed-based color selection
  let color1: string, color2: string, hotspotColor: string;

  if (themeColors && themeColors.length >= 2) {
    // Use theme colors
    color1 = themeColors[0];
    color2 = themeColors[1];
    hotspotColor = themeColors[2] || themeColors[1]; // Use third color if available, otherwise second
  } else {
    // Use shared color generation function
    const colors = generateColorsFromSeed(seed);
    color1 = colors.color1;
    color2 = colors.color2;
    hotspotColor = colors.hotspotColor || colors.color2;
  }

  const isDesktopWallpaper = surface === "desktop";
  const desktopUnderlay =
    isDesktopWallpaper && !dark && themeColors && themeColors.length >= 2
      ? mixHexColors(
          mixHexColors(ANIOS_THEME_UNDERLAY_LIGHT, themeColors[0], 0.22),
          themeColors[1],
          0.12,
        )
      : ANIOS_THEME_UNDERLAY_LIGHT;

  const bgColor = dark
    ? "transparent"
    : isDesktopWallpaper
      ? desktopUnderlay
      : color2;

  // Dark: soft blobs on a dark underlay at 40% layer opacity. Light desktop:
  // same accent colours at higher stop opacity on a theme-tinted underlay.
  const hotspotOpacity = dark ? 0.35 : isDesktopWallpaper ? 0.7 : 0.9;
  const stop1Opacity = dark ? 0.6 : isDesktopWallpaper ? 0.52 : 0.8;
  const stop2Opacity = dark || isDesktopWallpaper ? 0 : 1;
  const outerStopColor =
    dark || !isDesktopWallpaper ? color2 : desktopUnderlay;

  // Sanitize seed for ID
  const sanitizedSeed = seed.replace(/[^a-zA-Z0-9-]/g, "");
  const gradientId = `blob-gradient-${sanitizedSeed}`;
  const filterId = `blob-filter-${sanitizedSeed}`;

  // Determine center point based on position prop
  let center = { x: 50, y: 50 };
  switch (position) {
    case "tl":
      center = { x: 20, y: 20 };
      break;
    case "t":
      center = { x: 50, y: 20 };
      break;
    case "tr":
      center = { x: 80, y: 20 };
      break;
    case "l":
      center = { x: 20, y: 50 };
      break;
    case "r":
      center = { x: 80, y: 50 };
      break;
    case "bl":
      center = { x: 20, y: 80 };
      break;
    case "b":
      center = { x: 50, y: 80 };
      break;
    case "br":
      center = { x: 80, y: 80 };
      break;
  }

  // When animated, blob should be centered. Position prop is for static blobs.
  const animationCenter = animated ? { x: 50, y: 50 } : center;

  // Animation properties
  const animRadius = 15 + (hash % 10);
  const animDur = animationDuration ?? 10 + ((hash >> 2) % 10);
  const p1 = { x: animationCenter.x, y: animationCenter.y - animRadius };
  const p2 = { x: animationCenter.x + animRadius, y: animationCenter.y };
  const p3 = { x: animationCenter.x, y: animationCenter.y + animRadius };
  const p4 = { x: animationCenter.x - animRadius, y: animationCenter.y };
  const cxValues = `${p1.x}%; ${p2.x}%; ${p3.x}%; ${p4.x}%; ${p1.x}%`;
  const cyValues = `${p1.y}%; ${p2.y}%; ${p3.y}%; ${p4.y}%; ${p1.y}%`;

  // Gradient & filter properties for a "cloudy watercolor" effect
  const r = 70 + ((hash >> 3) % 20); // 70 to 89
  const hotspotStop = 30 + ((hash >> 5) % 21); // 30% to 50%
  const stdDeviation = 1.5 + ((hash >> 4) % 11) / 10; // 1.5 to 2.5: Soften the hard edges
  const baseFrequency = (0.02 + (hash % 11) / 1000).toFixed(4); // 0.02 to 0.03: Medium detail
  const numOctaves = 3 + (hash % 2); // 3 or 4: More complex than a simple blob
  const scale = 40 + ((hash >> 2) % 21); // 40 to 60: Pronounced shapes
  const displacementScale =
    isDesktopWallpaper && !dark ? Math.min(78, scale + 14) : scale;

  // Rasterize the static filter chain to a PNG once, then reuse it as a cheap bitmap.
  useEffect(() => {
    if (!useRaster || !isVisible) return;
    if (typeof window === "undefined" || typeof Image === "undefined") return;

    let cancelled = false;
    const svgMarkup =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${RASTER_PX}" height="${RASTER_PX}" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" fill="none">` +
      `<defs>` +
      `<radialGradient id="${gradientId}" cx="${center.x}%" cy="${center.y}%" r="${r}%">` +
      `<stop stop-color="${hotspotColor}" stop-opacity="${hotspotOpacity}"/>` +
      `<stop stop-color="${color1}" stop-opacity="${stop1Opacity}" offset="${hotspotStop}%"/>` +
      `<stop offset="100%" stop-color="${outerStopColor}" stop-opacity="${stop2Opacity}"/>` +
      `</radialGradient>` +
      `<filter id="${filterId}">` +
      `<feTurbulence type="fractalNoise" baseFrequency="${baseFrequency}" numOctaves="${numOctaves}" stitchTiles="stitch" result="turbulence"/>` +
      `<feDisplacementMap in="SourceGraphic" in2="turbulence" scale="${displacementScale}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>` +
      `<feColorMatrix in="displaced" type="matrix" values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 100 -50" result="hard_alpha"/>` +
      `<feGaussianBlur in="hard_alpha" stdDeviation="${stdDeviation}"/>` +
      `</filter>` +
      `</defs>` +
      `<rect width="100" height="100" fill="${bgColor}"/>` +
      `<rect width="100" height="100" fill="url(#${gradientId})" filter="url(#${filterId})"/>` +
      `</svg>`;

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = RASTER_PX;
        canvas.height = RASTER_PX;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          setRasterFailed(true);
          return;
        }
        ctx.drawImage(img, 0, 0, RASTER_PX, RASTER_PX);
        setRasterUrl(canvas.toDataURL("image/png"));
      } catch {
        // Tainted canvas or unsupported — fall back to the live SVG render.
        setRasterFailed(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) setRasterFailed(true);
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;

    return () => {
      cancelled = true;
    };
  }, [
    useRaster,
    isVisible,
    gradientId,
    filterId,
    center.x,
    center.y,
    r,
    hotspotColor,
    hotspotOpacity,
    color1,
    stop1Opacity,
    hotspotStop,
    color2,
    outerStopColor,
    stop2Opacity,
    baseFrequency,
    numOctaves,
    scale,
    displacementScale,
    stdDeviation,
    bgColor,
    dark,
    desktopUnderlay,
  ]);

  // Rasterized path: one-time PNG, no SVG filter in the live render tree.
  if (useRaster && !rasterFailed) {
    return (
      <div ref={containerRef} className={` overflow-hidden ${className}`}>
        {rasterUrl ? (
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url(${rasterUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        ) : (
          <div className="absolute inset-0 z-0" style={{ backgroundColor: color2 }} />
        )}
        {children && <div className="relative z-10">{children}</div>}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={` overflow-hidden ${className}`}>
      {isVisible ? (
        <div className="absolute inset-0 z-0">
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid slice"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <radialGradient
                id={gradientId}
                cx={animated ? `${p1.x}%` : `${center.x}%`}
                cy={animated ? `${p1.y}%` : `${center.y}%`}
                r={`${r}%`}
              >
                {animated && (
                  <>
                    <animate
                      attributeName="cx"
                      values={cxValues}
                      dur={`${animDur}s`}
                      repeatCount="indefinite"
                      calcMode="spline"
                      keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"
                      begin="0s"
                    />
                    <animate
                      attributeName="cy"
                      values={cyValues}
                      dur={`${animDur}s`}
                      repeatCount="indefinite"
                      calcMode="spline"
                      keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"
                      begin="0s"
                    />
                  </>
                )}
                <stop stopColor={hotspotColor} stopOpacity={hotspotOpacity} />
                <stop
                  stopColor={color1}
                  stopOpacity={stop1Opacity}
                  offset={`${hotspotStop}%`}
                />
                <stop
                  offset="100%"
                  stopColor={outerStopColor}
                  stopOpacity={stop2Opacity}
                />
              </radialGradient>
              <filter id={filterId}>
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency={baseFrequency}
                  numOctaves={numOctaves}
                  stitchTiles="stitch"
                  result="turbulence"
                />
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="turbulence"
                  scale={displacementScale}
                  xChannelSelector="R"
                  yChannelSelector="G"
                  result="displaced"
                />
                <feColorMatrix
                  in="displaced"
                  type="matrix"
                  values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 100 -50"
                  result="hard_alpha"
                />
                <feGaussianBlur in="hard_alpha" stdDeviation={stdDeviation} />
              </filter>
            </defs>
            <rect width="100" height="100" fill={bgColor} />
            <rect
              width="100"
              height="100"
              fill={`url(#${gradientId})`}
              filter={`url(#${filterId})`}
            />
          </svg>
        </div>
      ) : (
        // Lightweight placeholder until visible
        <div
          className="absolute inset-0 z-0"
          style={{ backgroundColor: color2 }}
        />
      )}
      {children && <div className="relative z-10">{children}</div>}
    </div>
  );
});

export default GradientBlobContainer;
